"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  ChevronDown,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Settings2,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import { performLogout } from "@/lib/logout";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

const menuIconClass = "h-[18px] w-[18px] shrink-0";

function getInitials(name, email) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  const fallback = parts[0] || String(email || "").trim();
  return fallback.slice(0, 2).toUpperCase() || "A";
}

function matchesLocation(href, currentLocation) {
  if (!currentLocation) return false;

  const [targetPath, targetQuery = ""] = href.split("?");
  const [currentPath, currentQuery = ""] = currentLocation.split("?");
  if (targetPath !== currentPath) return false;
  if (!targetQuery) return true;

  const targetParams = new URLSearchParams(targetQuery);
  const currentParams = new URLSearchParams(currentQuery);
  return Array.from(targetParams.entries()).every(
    ([key, value]) => currentParams.get(key) === value,
  );
}

/**
 * Shared premium account menu used by the public and onboarding headers.
 */
export default function ProfileMenu({ user = {}, onLogout }) {
  const [open, setOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("");
  const ref = useRef(null);
  const menuId = useId();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "profile"), [locale]);
  const nav = useMemo(() => getStrings(locale, "nav"), [locale]);

  useEffect(() => {
    function onDocClick(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const displayName = user?.name || "Account";
  const displayEmail = user?.email || "";
  const initials = useMemo(
    () => getInitials(displayName, displayEmail),
    [displayEmail, displayName],
  );

  const items = useMemo(
    () => [
      {
        href: "/onboarding?tab=profile",
        label: t.profile,
        Icon: UserRound,
      },
      {
        href: "/profile?tab=dashboard",
        label: t.dashboard,
        Icon: LayoutDashboard,
      },
      {
        href: "/interview",
        label: t.interview_copilot,
        Icon: Sparkles,
      },
      {
        href: "/profile?tab=billing",
        label: t.billing,
        Icon: CreditCard,
      },
      {
        href: "/profile?tab=automation",
        label: t.automation,
        Icon: Settings2,
      },
      {
        href: "/profile?tab=privacy",
        label: t.privacy_security,
        Icon: ShieldCheck,
      },
    ],
    [t],
  );

  const handleToggle = () => {
    const nextOpen = !open;
    if (nextOpen && typeof window !== "undefined") {
      setCurrentLocation(`${window.location.pathname}${window.location.search}`);
    }
    setOpen(nextOpen);
  };

  const handleLogout = async () => {
    setOpen(false);
    if (onLogout) return onLogout();

    try {
      await performLogout("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t.account_menu}
        className="group inline-flex max-w-[12rem] items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1.5 text-sm font-bold text-slate-800 shadow-sm transition hover:border-violet-200 hover:bg-violet-50/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] focus-visible:ring-offset-2 sm:max-w-[15rem] sm:px-3"
      >
        <span
          aria-hidden="true"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-violet-400 via-[#7658ed] to-[#4b22c9] text-[10px] font-black tracking-wide text-white shadow-[0_6px_14px_rgba(90,74,244,0.28)]"
        >
          {initials}
        </span>
        <span className="truncate">{displayName}</span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:text-[#5A4AF4] ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t.account_menu}
          className="absolute right-0 top-full z-[70] mt-2.5 w-[20rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-[0_28px_75px_-24px_rgba(48,31,104,0.42),0_16px_34px_-22px_rgba(15,23,42,0.32)]"
        >
          <div className="relative overflow-hidden border-b border-violet-100 bg-gradient-to-br from-violet-50 via-white to-indigo-50/70 px-4 py-4">
            <div
              aria-hidden="true"
              className="absolute -right-8 -top-12 h-28 w-28 rounded-full bg-violet-200/30 blur-2xl"
            />
            <div className="relative flex min-w-0 items-center gap-3">
              <div
                aria-hidden="true"
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gradient-to-br from-violet-300 via-[#7959ee] to-[#4b22c9] text-sm font-black tracking-[0.08em] text-white shadow-[0_10px_25px_rgba(90,74,244,0.30)] ring-4 ring-white"
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-slate-950">
                  {displayName}
                </p>
                {displayEmail ? (
                  <p
                    title={displayEmail}
                    className="mt-0.5 truncate text-xs font-medium text-slate-500"
                  >
                    {displayEmail}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-1 p-2.5">
            {items.map(({ href, label, Icon }) => {
              const active = matchesLocation(href, currentLocation);
              return (
                <Link
                  key={href}
                  href={href}
                  role="menuitem"
                  aria-current={active ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  className={`group/item relative flex items-center gap-3 overflow-hidden rounded-xl px-3 py-2.5 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5A4AF4] ${
                    active
                      ? "bg-violet-50 text-[#4f37bd]"
                      : "text-slate-700 hover:bg-slate-50 hover:text-[#4f37bd]"
                  }`}
                >
                  {active ? (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-1.5 left-0 w-0.5 rounded-r-full bg-[#6947df]"
                    />
                  ) : null}
                  <Icon
                    aria-hidden="true"
                    className={`${menuIconClass} ${
                      active
                        ? "text-[#6947df]"
                        : "text-violet-500 transition group-hover/item:text-[#6947df]"
                    }`}
                  />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>

          <div className="border-t border-slate-100 p-2.5">
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-bold text-rose-600 transition hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
            >
              <LogOut aria-hidden="true" className={menuIconClass} />
              <span>{nav.logout}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
